import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  ReorderAction,
  ReorderButtonsComponent,
} from '../reorder-buttons/reorder-buttons.component';
import { TrashButtonComponent } from '../trash-button/trash-button.component';
import { VesselTank } from '../vessel-tank.model';
import { TankSummaryComponent } from '../tank-transfer/tank-summary/tank-summary.component';

function getCurrentDateTimeFormatted() {
  const now = new Date();

  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
  const day = now.getDate().toString().padStart(2, '0');
  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');

  return `${year}${month}${day}${hours}${minutes}`;
}

export interface TankBunker {
  tank: VesselTank;
  fuel: {
    fuelGrade: string;
    quantity: number;
  };
}

@Component({
  selector: 're-tank-bunker',
  templateUrl: './tank-bunker.component.html',
  styleUrl: './tank-bunker.component.css',
  host: { '(document:keydown.escape)': 'onEscapeKey()' },
  imports: [
    ReactiveFormsModule,
    ReorderButtonsComponent,
    TrashButtonComponent,
    FormsModule,
    TankSummaryComponent,
  ],
})
export class TankBunkerComponent {
  @Input({ required: true }) from!: VesselTank;
  @Input({ required: true }) action: any;
  @Input({ required: true }) tanks!: VesselTank[];
  @Output() tankOperation = new EventEmitter();
  @Output() delete = new EventEmitter<any>();
  @Output() reorder = new EventEmitter<{ id: number; action: ReorderAction }>();

  selectedFuelGrade = '';
  selectedTankId = null;
  selectedTanks: TankBunker[] = [];

  fromProgress = 0;

  fuelGrades = ['HFO', 'LFO', 'MGO'];

  isoFuelGrades = ['RME180', 'RMG180', 'RMG380', 'RMG500', 'RMG700'];

  newBdn = {
    fuelGrade: null,
    isoFuelGrade: null,
    quantityOrdered: null,
    quantityReceived: null,
    bdnFigures: null,
    density: null,
    sulphur: null,
    viscosity: null,
    lcv: null,
    eValue: null,
    sulphurLabAnalysis: null,
    stableUntil: null,
    wttEmmisionFactor: null,
    cfCo2: null,
    cfCh4: null,
    cfN2o: null,
    cSlip: null,
  };

  isModalDisplayed = false;

  get bdn() {
    if (!this.newBdn.fuelGrade) return '';

    return `BDN${getCurrentDateTimeFormatted()}${this.newBdn.fuelGrade}`;
  }

  get orderedTanks() {
    return this.selectedTanks.sort((a, b) => a.tank.number - b.tank.number);
  }

  get availableTanks() {
    const selectedTankIds = this.selectedTanks.map((a) => a.tank.id);

    return this.tanks.filter((a) => selectedTankIds.indexOf(a.id) === -1);
  }

  get isTankDropdownDisplayed() {
    return this.selectedTanks.length !== this.tanks.length;
  }

  // Only a click on the backdrop itself closes the modal — a click inside the
  // dialog reaches here by bubbling, and must not.
  onBackdropClick(e: Event) {
    if (e.target === e.currentTarget) {
      this.onCloseModal();
    }
  }

  onCloseModal() {
    this.isModalDisplayed = false;
  }

  onEscapeKey() {
    if (this.isModalDisplayed) {
      this.onCloseModal();
    }
  }

  onDelete() {
    this.delete.emit(this.action);
  }

  onReorder(action: any) {
    this.reorder.emit({
      id: this.action.id,
      action: action,
    });
  }

  resetTank(tankBunker: TankBunker) {
    tankBunker.fuel.quantity = 0;
    this.onModelChange();
  }

  fillTank(tankBunker: TankBunker) {
    tankBunker.fuel.quantity =
      tankBunker.tank.capacity - this.tankQuantity(tankBunker);
    this.onModelChange();
  }

  tankQuantity(tankBunker: TankBunker) {
    return tankBunker.tank.content?.quantity || 0;
  }

  updateTankQuantity(tankBunker: TankBunker) {
    return this.tankQuantity(tankBunker) + tankBunker.fuel.quantity;
  }

  maxCapacity(tankBunker: TankBunker) {
    return tankBunker.tank.capacity - this.tankQuantity(tankBunker);
  }

  tankProgress(tankBunker: TankBunker) {
    const quantity = this.tankQuantity(tankBunker);

    return (quantity / tankBunker.tank.capacity) * 100;
  }

  tankProgress2(tankBunker: TankBunker) {
    return (tankBunker.fuel.quantity / tankBunker.tank.capacity) * 100;
  }

  onAddBunker() {
    this.isModalDisplayed = true;
  }

  onModelChange() {
    this.action.tankOperations = this.selectedTanks;
    // this.tankOperation.emit(this.selectedTanks);
  }

  onSelectTank() {
    if (!this.selectedTankId) return;

    const selectedTank = this.tanks.find(
      (a: VesselTank) => a.id === this.selectedTankId,
    );

    if (!selectedTank) return;

    this.selectedTanks = [
      ...this.selectedTanks,
      {
        tank: selectedTank,
        fuel: {
          fuelGrade: this.newBdn.fuelGrade || '',
          quantity: 0,
        },
      },
    ];

    this.selectedTankId = null;
  }

  onCancelBunker() {
    this.isModalDisplayed = false;
  }

  onSaveBdn() {
    this.onCloseModal();
  }
}
